import i18n from '@i18next/i18n';
import { observable, reaction } from 'mobx';
import { ToolbarType } from '@src/js/constant';
import SearchReplaceBuilder, { SearchReplace } from '@tencent/docs-scenario-components-search-replace-panel/dist';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { SearchOrder, SearchReplaceType, ISearchReplaceManager as ISearchReplaceManagerV3 } from './search-replace-manager-v3.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { DataType } from '@tencent/slide-sdk-model';
import { IToolbarStateManager } from '../toolbar-state-manager/core/toolbar-state-manager.interface';

export interface ISearchResult {
    current?: number;
    total?: number;
    canSingleReplace?: boolean;
    canReplaceAll?: boolean;
    message?: string;
    placeholder?: string;
    isHide?: boolean;
}

export enum SearchMode {
    BASIC_SEARCH = 0,
    ADVANCED_SEARCH = 1,
}

const BLUR = 'blur';
const FOCUS = 'focus';
const MOUSEDOWN = 'mousedown';
const RESULT_IN_NOTE = i18n.t('查找结果在备注内');
const SEARCH_INPUT = 'search-input';
const SEARCH_REPLACE_DIALOG = 'search-replace-component-dialog';
const DIALOG_INDEX = 0;
export const DOUBLE_TOOLBAR_PANEL_TOP = '167px';
export const SINGLE_TOOLBAR_PANEL_TOP = '103px';
export const PanelStyle = {
    [ToolbarType.SINGLE_TOOLBAR]: { top: SINGLE_TOOLBAR_PANEL_TOP },
    [ToolbarType.DOUBLE_TOOLBAR]: { top: DOUBLE_TOOLBAR_PANEL_TOP },
};

interface IDependentServices {
    selectionService: ISelectionService;
    uiDataService: IUiDataService;
    searchReplaceManager: ISearchReplaceManagerV3;
    userPriviledgeService: IUserPriviledgeService;
    masterLayoutEditService: IMasterLayoutEditService;
    toolbarStateManager: IToolbarStateManager;
}

export default class SearchReplaceDialogController {
    private searchReplacePanel: SearchReplace;
    private searchReplaceManager: ISearchReplaceManagerV3;
    private canEdit: boolean;
    private isFocus = false;
    private showPanel = false;
    private searchText = '';
    private dialog: any;
    private currentSlideIndex = -1;
    private slideCount = 1;
    private searchReplaceBuilder;
    private selectionService: ISelectionService;
    private uiDataService: IUiDataService;
    private userPriviledgeService: IUserPriviledgeService;
    private masterLayoutEditService: IMasterLayoutEditService;
    private toolbarStateManager: IToolbarStateManager;
    @observable private showToast = false;
    @observable private updateCount = 0; // 需要更新的情况: 当输入内容变化或者执行上一个/下一个按钮操作时，当前索引和查找到的总数不变的情况

    public init = (dependentServices: IDependentServices): void => {
        const {
            selectionService,
            uiDataService,
            userPriviledgeService,
            masterLayoutEditService,
            searchReplaceManager,
            toolbarStateManager,
        } = dependentServices;
        this.searchReplaceManager = searchReplaceManager;
        this.selectionService = selectionService;
        this.uiDataService = uiDataService;
        this.userPriviledgeService = userPriviledgeService;
        this.masterLayoutEditService = masterLayoutEditService;
        this.toolbarStateManager = toolbarStateManager;

        // 展示或者关闭查找替换面板
        reaction(
            () => ({
                dialogOpenFlag: this.searchReplaceManager?.dialogOpenFlag,
            }),
            this.handlePanelShowOrHide,
        );

        // 切换页面
        reaction(
            () => ({
                changePageFlag: this.searchReplaceManager?.changePageFlag,
                currentSlideIndex: this.selectionService.currentSlideIndex,
                slideCount: this.uiDataService.presentation.slideCount,
            }),
            this.handleSlideChange,
        );

        // 更新查找替换面板
        reaction(
            () => ({
                isSearchingFlag: this.searchReplaceManager?.isSearchingFlag,
                keywordCount: this.searchReplaceManager?.keywordCount,
                updateCount: this.updateCount,
            }),
            this.updatePanel,
        );

        // 权限更新
        reaction(
            () => ({
                pptEditorState: this.userPriviledgeService.pptEditorState,
            }),
            this.lockChange,
        );

        // 工具栏 单双栏切换
        reaction(
            () => ({
                toolbarMode: this.toolbarStateManager.getToolbarMode(),
            }),
            this.updatePanelStyle,
        );

        reaction(
            () => this.masterLayoutEditService?.isMasterEditType ?? false,
            (isMasterEditType: boolean) => isMasterEditType && this.hidePanel(),
        );
    };

    /**
     * 使用builder创建查找替换实例
     */
    private createSearchReplacePanel = (): SearchReplace => {
        // 创建builder实例
        this.searchReplaceBuilder = new SearchReplaceBuilder(this.canEdit);
        this.searchReplaceBuilder.setSearch({
            handleSearch: this.handleSearch,
            handleInputChange: (searchText: string) => this.handleValueChange(searchText),
            handleNext: () => this.handleHighlight(SearchOrder.Next),
            handlePrev: () => this.handleHighlight(SearchOrder.Last),
            handleClose: this.handleClose,
        });
        // 拥有可编辑权限时增加替换面板
        if (this.canEdit) {
            this.searchReplaceBuilder.setReplace({
                handleReplace: (replaceText: string) => this.handleReplace(replaceText),
                handleReplaceAll: (replaceText: string) => this.handleReplaceAll(replaceText),
            });
        }
        return this.searchReplaceBuilder.build();
    };

    /**
     * 控制面板的显示 或 隐藏
     */
    private handlePanelShowOrHide = (): void => {
        // 初始化构建面板
        if (!this.searchReplacePanel) {
            this.lockChange();
        }
        const showFlag = this.searchReplaceManager?.dialogOpenFlag || false;
        if (showFlag) {
            this.showPanel = true;
            const searchMode = this.searchReplaceManager?.searchReplaceType === SearchReplaceType.Replace
                ? SearchMode.ADVANCED_SEARCH
                : SearchMode.BASIC_SEARCH;
            this.searchReplacePanel?.show(searchMode, document.body, this.getPanelStyle());
            this.dialog = document.getElementsByClassName(SEARCH_REPLACE_DIALOG)[DIALOG_INDEX];
            this.mountEvent();
        } else {
            this.hidePanel();
        }
    };

    private hidePanel = (): void => {
        this.closePanel();
        this.unmountEvent();
        this.dialog = null;
    };

    private handleSlideChange = (): void => {
        const { currentSlideIndex } = this.selectionService;
        if (
            currentSlideIndex !== this.currentSlideIndex
            && this.searchReplaceManager?.isSearchingFlag
            && !this.searchReplaceManager?.changePageFlag
        ) {
            this.searchReplaceManager?.exitSearching();
        }
        this.currentSlideIndex = currentSlideIndex;

        const currentSlideCount = this.uiDataService.presentation.slideCount;
        if (currentSlideCount !== this.slideCount) {
            this.slideCount = currentSlideCount;
            this.handleDialogFocus();
        }
    };

    private updatePanel = (): ISearchResult | void => {
        if (this.updateCount === 0) return;
        return this.updatePanelStyle();
    };

    private updatePanelStyle = (): ISearchResult | void => {
        const searchResult = this.getSearchResult();
        this.searchReplacePanel?.update(searchResult, this.getPanelStyle());
        return searchResult;
    };

    /**
     * 监听权限变更
     */
    private lockChange = (): void => {
        this.canEdit = this.userPriviledgeService.pptEditorState === PPTEditorStateType.EDITABLE;
        if (this.searchReplacePanel) {
            // 权限变更后销毁组件，并且关闭面板
            this.searchReplaceManager?.closeDialog();
            this.searchReplacePanel?.close();
            this.searchReplacePanel?.destroy();
        }
        this.searchReplacePanel = this.createSearchReplacePanel();
    };

    private mountEvent(): void {
        const searchInput = document.getElementById(SEARCH_INPUT);
        searchInput?.addEventListener(FOCUS, this.handleDialogFocus, true);
        searchInput?.addEventListener(BLUR, this.handleDialogBlur, true);
        window.addEventListener(MOUSEDOWN, this.handleDialogMouseDown, true);
    }

    private unmountEvent(): void {
        const searchInput = document.getElementById(SEARCH_INPUT);
        searchInput?.removeEventListener(FOCUS, this.handleDialogFocus, true);
        searchInput?.removeEventListener(BLUR, this.handleDialogBlur, true);
        window.removeEventListener(MOUSEDOWN, this.handleDialogMouseDown, true);
    }

    private handleDialogFocus = (): void => {
        if (this.isFocus) return;
        this.isFocus = true;
        this.selectionService.selectedElements?.clear();
        this.research();
    };

    private handleDialogBlur = (): void => {
        if (!this.isFocus) return;
        this.isFocus = false;
    };

    private handleDialogMouseDown = (e): void => {
        if (this.searchReplaceManager.modal || !this.dialog) return;
        if (this.dialog?.contains(e.target)) {
            this.handleDialogFocus();
        } else {
            this.handleDialogBlur();
            this.exitSearching();
        }
    };

    /**
     * 由页面变更等原因导致需要重新搜索
     */
    private research = (): void => {
        if (this.searchText && !this.searchReplaceManager?.isSearchingFlag) {
            this.searchReplaceManager?.search(this.searchText);
        }
    };

    private getSearchResult = (): ISearchResult => ({
        current: this.getCurrentIndex(),
        total: this.keywordCount,
        canSingleReplace: this.keywordCount > 0,
        canReplaceAll: this.keywordCount > 0,
        message: this.showToast ? i18n.t(RESULT_IN_NOTE) : '',
    });

    private getCurrentIndex = (): number => {
        let currentIndex = this.searchReplaceManager?.currentIndex || 0;
        // 避免搜索结束，修改搜索内容，再返回查找替换面板时索引值大于总数的情况
        if (this.keywordCount !== 0 && currentIndex > this.keywordCount - 1) {
            currentIndex = this.keywordCount - 1;
        }
        return currentIndex;
    };

    private handleValueChange = async (searchText: string): Promise<void> => {
        this.searchText = searchText;
        // 如果删除查找文字，清除所有高亮的内容和提示
        if (!this.searchText) {
            this.searchReplaceManager?.clear();
            this.clearToast();
        }
        this.searchReplaceManager?.search(this.searchText);
        this.updateCount += 1;
    };

    private handleHighlight = async (order: SearchOrder): Promise<void> => {
        this.clearToast();

        if (this.shouldStopSearch()) {
            this.updatePanel();
            return;
        }
        if (!this.searchReplaceManager?.isSearchingFlag) {
            // 如果没在search，先search一次
            this.searchReplaceManager?.search(this.searchText);
        }
        this.checkNoteArea(order);
        this.handleDialogFocus();
        this.updateCount += 1;
    };

    /**
     * 检查查找内容是否在备注区
     * @param order “上一个”或者“下一个”按钮操作
     */
    private checkNoteArea(order: SearchOrder): void {
        const highLightPosition = this.searchReplaceManager?.searchHighlight(order);
        if (highLightPosition) {
            const { component } = highLightPosition;
            if (component?.getPage().type === DataType.Notes) {
                this.showToast = true;
            }
        }
    }

    private handleSearch = async (): Promise<void> => {
        if (this.searchReplaceManager?.modal) return; // 在交互状态下不需要处理
        if (this.shouldStopSearch()) {
            // 避免查找状态下，查找内容无结果时，切换到灰色页
            this.updatePanel();
            return;
        }
        this.handleHighlight(SearchOrder.Next);
    };

    // 避免查找状态下，查找内容无结果时，切换到灰色页
    private shouldStopSearch = () => (this.searchReplaceManager
        ?.isSearchingFlag && this.keywordCount === 0) || this.searchText === '';

    private handleReplace = async (replaceText: string): Promise<void> => {
        this.clearToast();

        if (!this.searchReplaceManager?.lastHighlightPosition) {
            // 第一次查询
            this.handleHighlight(SearchOrder.Next);
        } else {
            await this.searchReplaceManager?.replace(replaceText);
        }
        this.handleDialogFocus();
    };

    private handleReplaceAll = async (replaceText: string): Promise<void> => {
        if (this.searchReplaceManager?.modal) return; // 在交互状态下不需要处理

        this.clearToast();

        if (!this.searchReplaceManager?.isSearchingFlag) {
            this.searchReplaceManager?.search(this.searchText);
        }
        this.searchReplaceManager?.replaceAll(replaceText);
        this.handleDialogFocus();
    };

    private handleClose = (): void => {
        this.searchReplaceManager?.closeDialog();
    };

    private closePanel = (): void => {
        if (!this.showPanel) return;

        this.searchReplacePanel?.close();
        this.showPanel = false;
        this.searchText = '';
        this.updateCount = 0;
        this.clearToast();
        this.exitSearching();
    };

    private exitSearching(): void {
        const isSearchingFlag = this.searchReplaceManager?.isSearchingFlag || false;
        if (isSearchingFlag) {
            this.searchReplaceManager?.exitSearching();
        }
    }

    /**
     * 清除查找内容在备注区的提示
     */
    private clearToast = (): void => {
        this.showToast = false;
    };

    /**
     * 根据工具栏是单栏还是双栏 决定面板的位置
     * @returns 查找替换面板的偏移位置
     */
    private getPanelStyle = (): React.CSSProperties => {
        const res = PanelStyle[this.toolbarStateManager.getToolbarMode()];
        if (res) {
            return res;
        }
        return { top: DOUBLE_TOOLBAR_PANEL_TOP };
    };

    private get keywordCount(): number {
        return this.searchReplaceManager?.keywordCount || 0;
    }
}
